import type { EventClickArg, EventDropArg, EventInput } from '@fullcalendar/core'
import type { ShownEvent } from '@/hooks/useEvents'
import type { People } from '@/hooks/usePeople'
import { eventTone } from '@/components/eventTone'
import { gridEntries, stepDate, type GridEntry } from '@/logic/calendar'
import { dateOf, fmtDate, today } from '@/logic/dates'
import { gridEventEntries, type GridEventEntry } from '@/logic/events'
import { openEventCard, openQuickAdd, setCalendarDate, setCalendarMode, useUi } from '@/store/ui'
import type { Category, DateStr, Task } from '@/types'

// What the Calendar's modes share: moving by a period, opening a day, adding
// for a day, and turning tasks and events into FullCalendar entries and back.
// Events are read-only everywhere; only a task's Do it on day moves.

export const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

/** Moves the selected day by one period of the mode shown. Stable, so swipe handlers can hold it. */
function step(dir: 1 | -1): void {
  const { calendarMode, calendarDate } = useUi.getState()
  setCalendarDate(stepDate(calendarMode ?? 'day', calendarDate, dir, today()))
}

export const nextPeriod = () => step(1)
export const prevPeriod = () => step(-1)

/** Day view for one day, from a month's date, its empty space or "+N more". */
export function openDay(day: DateStr): void {
  setCalendarDate(day)
  setCalendarMode('day')
}

export function addFor(day: DateStr): void {
  openQuickAdd({ plannedDate: day, hint: `Add for ${fmtDate(day)}` })
}

/** A task's ring and outline colour: its category's palette token, or none. */
export function categoryColor(byId: Record<string, Category>): (t: Task) => string | null {
  return (t) => (t.categoryId && byId[t.categoryId] ? `var(--${byId[t.categoryId].color})` : null)
}

export type EntryProps = GridEntry['extendedProps'] | GridEventEntry['extendedProps']

/**
 * Tasks and events within a range as FullCalendar entries. All-day events sit
 * above the day's tasks in the all-day row and a month's day; tasks keep the
 * Plan tab's order. Each event carries its owner's tone class, which sets --pc.
 */
export function calendarEntries(tasks: Task[], events: ShownEvent[], from: DateStr, to: DateStr, todayStr: DateStr, colorOf: (t: Task) => string | null, people: People, hideDone = false, timedOnly = false): EventInput[] {
  const owners = new Map(events.map((e) => [`${e.calendarId}:${e.id}`, e.owner]))
  const taskEntries = gridEntries(tasks, from, to, todayStr, colorOf, hideDone, timedOnly).map((e, i) => ({
    ...e,
    rank: i,
    classNames: ['fcx-entry', 'fcx-entry--task'],
  }))
  const eventEntries = gridEventEntries(events, from, to, (e) => owners.get(`${e.calendarId}:${e.id}`) ?? 'both').map((e, i) => ({
    ...e,
    rank: i - events.length,
    classNames: ['fcx-entry', 'fcx-entry--event', `ev--${eventTone(e.extendedProps.owner, people)}`],
  }))
  return [...eventEntries, ...taskEntries]
}

/** A tap on an entry: an event opens its card; a task opens, read-only when it is a preview. */
export function openEntry(info: EventClickArg, openTask: (id: string, readOnly: boolean) => void): void {
  info.jsEvent.preventDefault()
  const p = info.event.extendedProps as EntryProps
  if (p.kind === 'event') openEventCard({ calendarId: p.calendarId, eventId: p.eventId })
  else openTask(p.taskId, p.item.faint)
}

/**
 * A task dropped on another day is planned there; its due date and time never
 * change. The grid's own copy always snaps back, so where the task lands is
 * decided by the Plan rules alone: a timed task moved to another day goes to
 * that day's all-day row.
 */
export function dropOnDay(info: EventDropArg, planOn: (task: Task, day: DateStr) => void): void {
  const p = info.event.extendedProps as EntryProps
  const day = info.event.start ? dateOf(info.event.start) : null
  const was = info.oldEvent.start ? dateOf(info.oldEvent.start) : null
  info.revert()
  if (p.kind !== 'task' || p.item.done || p.item.faint || !day || day === was) return
  planOn(p.item.task, day)
}
